/**
 * Contract fixtures. The client develops against these before the API exists; the API's
 * contract tests assert its real responses parse with the same schemas.
 * Coordinates are approximate and for development only — not real survey data. The
 * "enteneck" entry sits on the real Gunten shore of Thunersee (verified against
 * swissBATHY3D by the API team); object depths are illustrative.
 */
import type {
  DiveLakeList,
  DiveManifest,
  DiveObject,
  Lake,
  SpotDetail,
  SpotListItem,
  Suggestion,
  WindField,
  PrecipField,
} from "../src/index.ts";
import {
  DIVE_CELL_SIZE_M,
  DIVE_OVERVIEW_MAX_SIDE,
  DIVE_TILE_CELLS,
  DIVE_TILE_SIDE,
  diveTileOriginLv95,
  EMPTY_RATINGS,
  lv95ToWgs84,
  TERRAIN_MAX_SIDE,
  TERRAIN_SOURCE,
  wgs84ToLv95,
  WIND_ATTRIBUTION,
  WIND_GRID,
  WIND_HOURS,
  WIND_UNIT_MS,
  PRECIP_ATTRIBUTION,
  precipUnits,
  windUV,
} from "../src/index.ts";
import type { Terrain } from "../src/terrain-codec.ts";

const T = "2026-10-01T10:00:00.000Z";

export const lakesFixture: Lake[] = [
  {
    id: "0b8e4f7e-3c1a-4e2b-9a51-7d1c2e3f4a01",
    slug: "thunersee",
    name: { de: "Thunersee", fr: "Lac de Thoune", it: "Lago di Thun", en: "Lake Thun" },
    waterLevelM: 557.7,
    hasBathy: true,
    resolutionM: 1,
    bbox: [7.62, 46.64, 7.88, 46.77],
  },
  {
    id: "0b8e4f7e-3c1a-4e2b-9a51-7d1c2e3f4a02",
    slug: "rotsee",
    name: { de: "Rotsee" },
    waterLevelM: 419.0,
    hasBathy: true,
    resolutionM: 1,
    bbox: [8.29, 47.07, 8.33, 47.08],
  },
];

const objectsThun: DiveObject[] = [
  {
    id: "5f1d2c3b-4a59-4e6f-8a7b-9c0d1e2f3a01",
    spotId: "7a2b3c4d-5e6f-4a1b-8c9d-0e1f2a3b4c01",
    type: "figure",
    name: { de: "Taucher-Statue", en: "Diver statue" },
    description: { de: "Bronzefigur auf einem Betonsockel." },
    location: { lat: 46.7039, lon: 7.7284 },
    depthComputedM: 17.4,
    depthOverrideM: 18.0,
    depth: { valueM: 18.0, source: "override" },
    lastSighting: { seenOn: "2026-09-27", present: true },
    sightingCount: 4,
    status: "published",
    createdAt: T,
    updatedAt: T,
  },
  {
    id: "5f1d2c3b-4a59-4e6f-8a7b-9c0d1e2f3a02",
    spotId: "7a2b3c4d-5e6f-4a1b-8c9d-0e1f2a3b4c01",
    type: "toilet",
    name: { de: "Alte Toilette" },
    description: {},
    location: { lat: 46.70425, lon: 7.72875 },
    depthComputedM: 9.2,
    depthOverrideM: null,
    depth: { valueM: 9.2, source: "computed" },
    lastSighting: null,
    sightingCount: 0,
    status: "published",
    createdAt: T,
    updatedAt: T,
  },
  {
    id: "5f1d2c3b-4a59-4e6f-8a7b-9c0d1e2f3a10",
    spotId: "7a2b3c4d-5e6f-4a1b-8c9d-0e1f2a3b4c01",
    type: "wreck",
    name: { de: "Versunkenes Ruderboot mit sehr langem Namen für die Beschriftung" },
    description: {},
    location: { lat: 46.7041, lon: 7.7284 },
    depthComputedM: 14.6,
    depthOverrideM: null,
    depth: { valueM: 14.6, source: "computed" },
    lastSighting: null,
    sightingCount: 0,
    status: "published",
    createdAt: T,
    updatedAt: T,
  },
  {
    id: "5f1d2c3b-4a59-4e6f-8a7b-9c0d1e2f3a11",
    spotId: "7a2b3c4d-5e6f-4a1b-8c9d-0e1f2a3b4c01",
    type: "wreck",
    name: { de: "Seilbahn-Gondel" },
    description: {},
    location: { lat: 46.7042, lon: 7.7284 },
    depthComputedM: 22.1,
    depthOverrideM: null,
    depth: { valueM: 22.1, source: "computed" },
    lastSighting: null,
    sightingCount: 0,
    status: "published",
    createdAt: T,
    updatedAt: T,
  },
  {
    id: "5f1d2c3b-4a59-4e6f-8a7b-9c0d1e2f3a12",
    spotId: "7a2b3c4d-5e6f-4a1b-8c9d-0e1f2a3b4c01",
    type: "figure",
    name: { de: "Granitblock" },
    description: {},
    location: { lat: 46.7043, lon: 7.7284 },
    depthComputedM: 6.4,
    depthOverrideM: null,
    depth: { valueM: 6.4, source: "computed" },
    lastSighting: null,
    sightingCount: 0,
    status: "published",
    createdAt: T,
    updatedAt: T,
  },
];

export const spotDetailsFixture: SpotDetail[] = [
  {
    id: "7a2b3c4d-5e6f-4a1b-8c9d-0e1f2a3b4c01",
    slug: "enteneck",
    name: "Enteneck",
    location: { lat: 46.7045, lon: 7.729 },
    lakeSlug: "thunersee",
    hasBathy: true,
    waterBody: "Thunersee",
    locality: "Gunten",
    difficulty: "medium",
    maxDepthM: 40,
    closedPeriods: [],
    permanentClosure: null,
    objectCount: objectsThun.length,
    status: "published",
    parkingLocation: { lat: 46.7052, lon: 7.7297 },
    description: { de: "Steil abfallende Wand mit Objekten.", en: "Steep wall with objects." },
    access: { de: "Über die Seestrasse." },
    parking: { de: "Öffentliche Parkplätze, gebührenpflichtig." },
    entry: { de: "Über die Treppe beim Bootssteg." },
    hazards: { de: "Bootsverkehr, schnell zunehmende Tiefe." },
    restrictions: {},
    facilities: { de: "Restaurant in der Nähe." },
    ratings: { fish: 3, walls: 4, caves: 0, wrecksSculptures: 3, current: 1, surroundings: 4 },
    objects: objectsThun,
    createdAt: T,
    updatedAt: T,
  },
  {
    id: "7a2b3c4d-5e6f-4a1b-8c9d-0e1f2a3b4c02",
    slug: "sportplatz-diepoldsau",
    name: "Sportplatz",
    location: { lat: 47.382473, lon: 9.672431 },
    lakeSlug: null,
    hasBathy: false,
    waterBody: "Alter Rhein",
    locality: "Diepoldsau",
    difficulty: "very_easy",
    maxDepthM: 9,
    closedPeriods: [{ from: "11-15", to: "02-28", reason: { de: "Laichschonzeit" } }],
    permanentClosure: null,
    objectCount: 0,
    status: "published",
    parkingLocation: null,
    description: {},
    access: {},
    parking: {},
    entry: {},
    hazards: {},
    restrictions: {},
    facilities: {},
    ratings: EMPTY_RATINGS,
    objects: [],
    createdAt: T,
    updatedAt: T,
  },
  {
    id: "7a2b3c4d-5e6f-4a1b-8c9d-0e1f2a3b4c03",
    slug: "rotsee-nord",
    name: "Rotsee Nord",
    location: { lat: 47.0755, lon: 8.3135 },
    lakeSlug: "rotsee",
    hasBathy: true,
    waterBody: "Rotsee",
    locality: "Luzern",
    difficulty: null,
    maxDepthM: null,
    closedPeriods: [],
    permanentClosure: {
      since: "2024-06-01",
      reason: { de: "Tauchverbot der Gemeinde.", en: "Diving banned by the municipality." },
    },
    objectCount: 0,
    status: "draft",
    parkingLocation: null,
    description: {},
    access: {},
    parking: {},
    entry: {},
    hazards: {},
    restrictions: {},
    facilities: {},
    ratings: EMPTY_RATINGS,
    objects: [],
    createdAt: T,
    updatedAt: T,
  },
];

export const spotListFixture: SpotListItem[] = spotDetailsFixture.map(
  ({
    id,
    slug,
    name,
    location,
    lakeSlug,
    hasBathy,
    waterBody,
    locality,
    difficulty,
    maxDepthM,
    closedPeriods,
    permanentClosure,
    objectCount,
    status,
  }) => ({
    id,
    slug,
    name,
    location,
    lakeSlug,
    hasBathy,
    waterBody,
    locality,
    difficulty,
    maxDepthM,
    closedPeriods,
    permanentClosure,
    objectCount,
    status,
  }),
);

/**
 * Synthetic lakebed: shore on the north edge sloping into a bowl, with a nodata strip in
 * the shallows (as real swissBATHY3D has). Deterministic — safe for snapshot tests.
 */
export function makeTerrainFixture(radiusM = 150, requestedCellSizeM = 2): Terrain {
  // Same downsample rule the API must apply: never exceed TERRAIN_MAX_SIDE cells per side.
  const cellSizeM = Math.max(requestedCellSizeM, Math.ceil((2 * radiusM) / (TERRAIN_MAX_SIDE - 1)));
  const side = Math.floor((2 * radiusM) / cellSizeM) + 1;
  const center = { lat: 46.7045, lon: 7.729 };
  const c = wgs84ToLv95(center);
  const waterLevelM = 557.7;
  const elevation = new Float32Array(side * side);
  const source = new Uint8Array(side * side);
  let min = Infinity;
  let max = -Infinity;
  for (let r = 0; r < side; r++) {
    for (let col = 0; col < side; col++) {
      const i = r * side + col;
      const fromShore = r / (side - 1); // 0 at north edge
      if (fromShore < 0.15) {
        elevation[i] = waterLevelM + 2 + (0.15 - fromShore) * 40;
        source[i] = TERRAIN_SOURCE.land;
      } else if (fromShore < 0.2) {
        elevation[i] = Number.NaN;
        source[i] = TERRAIN_SOURCE.nodata;
        continue;
      } else {
        const dx = (col - side / 2) / side;
        elevation[i] = waterLevelM - 2 - (fromShore - 0.2) * 120 - 15 * Math.cos(dx * Math.PI);
        source[i] = TERRAIN_SOURCE.bathy;
      }
      min = Math.min(min, elevation[i]!);
      max = Math.max(max, elevation[i]!);
    }
  }
  return {
    meta: {
      width: side,
      height: side,
      cellSizeM,
      originLv95: { e: c.e - radiusM, n: c.n + radiusM },
      center,
      radiusM,
      lakeSlug: "thunersee",
      waterLevelM,
      minElevM: min,
      maxElevM: max,
      attribution: "© swisstopo",
    },
    elevation,
    source,
  };
}

const SuggestionSpotPayloadExample = {
  name: "Seesteg Merligen",
  location: { lat: 46.6975, lon: 7.7405 },
  waterBody: "Thunersee",
  locality: "Merligen",
  difficulty: "easy" as const,
  maxDepthM: 18,
  closedPeriods: [],
  parkingLocation: null,
  description: { de: "Flacher Einstieg über den Steg." },
  access: {},
  parking: {},
  entry: {},
  hazards: {},
  restrictions: {},
  facilities: {},
  ratings: EMPTY_RATINGS,
};

/** Diver suggestions in each interesting state (client develops the review queue on these). */
export const suggestionsFixture: Suggestion[] = [
  {
    id: "9c1d2e3f-4a5b-4c6d-8e7f-0a1b2c3d4e01",
    kind: "spot_create",
    status: "pending",
    targetId: null,
    payload: SuggestionSpotPayloadExample,
    note: "Kleiner Steg, gut für Anfänger.",
    submittedBy: { id: "diver-1", name: "Mia Taucherin" },
    reviewedBy: null,
    reviewReason: null,
    resultId: null,
    adjusted: false,
    targetUpdatedAt: null,
    createdAt: T,
    reviewedAt: null,
  },
  {
    id: "9c1d2e3f-4a5b-4c6d-8e7f-0a1b2c3d4e02",
    kind: "object_edit",
    status: "pending",
    targetId: "5f1d2c3b-4a59-4e6f-8a7b-9c0d1e2f3a02",
    payload: { depthOverrideM: 9.8 },
    note: "Mit dem Computer nachgemessen: 9.8 m.",
    submittedBy: { id: "diver-2", name: "Luca" },
    reviewedBy: null,
    reviewReason: null,
    resultId: null,
    adjusted: false,
    targetUpdatedAt: T,
    createdAt: T,
    reviewedAt: null,
  },
  {
    id: "9c1d2e3f-4a5b-4c6d-8e7f-0a1b2c3d4e03",
    kind: "spot_edit",
    status: "rejected",
    targetId: "7a2b3c4d-5e6f-4a1b-8c9d-0e1f2a3b4c01",
    payload: { maxDepthM: 60 },
    note: "Man kommt tiefer.",
    submittedBy: { id: "diver-1", name: "Mia Taucherin" },
    reviewedBy: { id: "mod-1", name: "Moderation" },
    reviewReason: "Max. Tiefe meint die empfohlene Tiefe am Platz, nicht die Seetiefe.",
    resultId: null,
    adjusted: false,
    targetUpdatedAt: T,
    createdAt: T,
    reviewedAt: T,
  },
];

// ---------------------------------------------------------------------------------------------
// Dive mode. A synthetic 3×2 tile set (768 × 512 m) on the Gunten shore of Thunersee around "enteneck": land
// to the north, an 8 m wide nodata strip (the shallow gap), then a lakebed sloping south to
// about 200 m depth. Tile (2, 1) is missing on purpose. Illustrative, not survey data.

const DIVE_FIXTURE_CELL_M = DIVE_CELL_SIZE_M;
const DIVE_FIXTURE_WATER_LEVEL_M = 557.7;
const diveFixtureCentre = wgs84ToLv95({ lat: 46.7045, lon: 7.729 });
const diveFixtureShoreN = diveFixtureCentre.n + 60;
const diveFixtureOrigin = {
  e: Math.round(diveFixtureCentre.e - 1.5 * DIVE_TILE_CELLS * DIVE_FIXTURE_CELL_M),
  n: Math.round(diveFixtureCentre.n + 300),
};
const DIVE_FIXTURE_TILES: [number, number][] = [
  [0, 0],
  [1, 0],
  [2, 0],
  [0, 1],
  [1, 1],
];

/** Elevation (LN02) at an LV95 point of the dive fixture; NaN = nodata. */
function diveFixtureElevation(e: number, n: number): number {
  const d = diveFixtureShoreN - n; // metres south of the shore line
  if (d < 0) return DIVE_FIXTURE_WATER_LEVEL_M + 2 + -d * 0.2;
  if (d < 8) return Number.NaN;
  return DIVE_FIXTURE_WATER_LEVEL_M - 2 - Math.min(d * 0.25, 200) - 5 * Math.sin(e / 40);
}

function diveFixtureGrid(
  e0: number,
  n0: number,
  width: number,
  height: number,
  cellSizeM: number,
): Terrain {
  const elevation = new Float32Array(width * height);
  const source = new Uint8Array(width * height);
  let min = Infinity;
  let max = -Infinity;
  for (let r = 0; r < height; r++) {
    for (let c = 0; c < width; c++) {
      const i = r * width + c;
      const v = diveFixtureElevation(e0 + c * cellSizeM, n0 - r * cellSizeM);
      elevation[i] = v;
      if (Number.isNaN(v)) continue;
      source[i] = v > DIVE_FIXTURE_WATER_LEVEL_M ? TERRAIN_SOURCE.land : TERRAIN_SOURCE.bathy;
      // Float32 rounding, so min/max match what decodeTerrain will read back.
      min = Math.min(min, elevation[i]!);
      max = Math.max(max, elevation[i]!);
    }
  }
  const half = ((width - 1) * cellSizeM) / 2;
  return {
    meta: {
      width,
      height,
      cellSizeM,
      originLv95: { e: e0, n: n0 },
      center: lv95ToWgs84({ e: e0 + half, n: n0 - ((height - 1) * cellSizeM) / 2 }),
      radiusM: Math.round(half),
      lakeSlug: "thunersee",
      waterLevelM: null,
      minElevM: min,
      maxElevM: max,
      attribution: "© swisstopo",
    },
    elevation,
    source,
  };
}

/** Tile (x, y) of the dive fixture, or null when the manifest does not list it (→ 404). */
export function makeDiveTileFixture(x: number, y: number): Terrain | null {
  if (!DIVE_FIXTURE_TILES.some(([tx, ty]) => tx === x && ty === y)) return null;
  const o = diveTileOriginLv95(
    { originLv95: diveFixtureOrigin, cellSizeM: DIVE_FIXTURE_CELL_M },
    x,
    y,
  );
  return diveFixtureGrid(o.e, o.n, DIVE_TILE_SIDE, DIVE_TILE_SIDE, DIVE_FIXTURE_CELL_M);
}

/** Whole-grid overview of the dive fixture (≤ DIVE_OVERVIEW_MAX_SIDE per side). */
export function makeDiveOverviewFixture(): Terrain {
  const cols = 3;
  const rows = 2;
  const spanM = DIVE_TILE_CELLS * DIVE_FIXTURE_CELL_M;
  const cellSizeM = Math.max(16, Math.ceil((cols * spanM) / (DIVE_OVERVIEW_MAX_SIDE - 1)));
  const width = Math.floor((cols * spanM) / cellSizeM) + 1;
  const height = Math.floor((rows * spanM) / cellSizeM) + 1;
  return diveFixtureGrid(diveFixtureOrigin.e, diveFixtureOrigin.n, width, height, cellSizeM);
}

function diveFixtureManifest(): DiveManifest {
  let min = Infinity;
  let max = -Infinity;
  for (const [x, y] of DIVE_FIXTURE_TILES) {
    const t = makeDiveTileFixture(x, y)!;
    min = Math.min(min, t.meta.minElevM);
    max = Math.max(max, t.meta.maxElevM);
  }
  return {
    lakeSlug: "thunersee",
    version: "fixture-1",
    builtAt: T,
    waterLevelM: DIVE_FIXTURE_WATER_LEVEL_M,
    cellSizeM: DIVE_FIXTURE_CELL_M,
    tileCells: DIVE_TILE_CELLS,
    originLv95: diveFixtureOrigin,
    cols: 3,
    rows: 2,
    tiles: DIVE_FIXTURE_TILES,
    minElevM: min,
    maxElevM: max,
    attribution: "© swisstopo",
  };
}

export const diveManifestFixture: DiveManifest = diveFixtureManifest();

export const diveLakesFixture: DiveLakeList = {
  lakes: [
    {
      slug: diveManifestFixture.lakeSlug,
      version: diveManifestFixture.version,
      builtAt: diveManifestFixture.builtAt,
      tileCount: diveManifestFixture.tiles.length,
    },
  ],
};

/**
 * Synthetic wind field (deterministic for a given hour): a Bise from the north-east that veers to
 * a westerly over the 24 h, stronger in the west (Léman) than in the east. `times` start at the
 * UTC hour containing `nowMs`, like the live API. A small no-data patch (null) sits in the far
 * south-east corner, away from every spot, so clients exercise "draw nothing there".
 */
export function makeWindFieldFixture(nowMs: number = Date.now()): WindField {
  const start = Math.floor(nowMs / 3_600_000) * 3_600_000;
  const g = WIND_GRID;
  const times: string[] = [];
  const u: (number | null)[][] = [];
  const v: (number | null)[][] = [];
  for (let k = 0; k < WIND_HOURS; k++) {
    times.push(new Date(start + k * 3_600_000).toISOString());
    const dir = 45 + (225 * k) / (WIND_HOURS - 1); // NE → W
    const uk: (number | null)[] = [];
    const vk: (number | null)[] = [];
    for (let j = 0; j < g.ny; j++)
      for (let i = 0; i < g.nx; i++) {
        if (i >= g.nx - 3 && j <= 1) {
          uk.push(null);
          vk.push(null);
          continue;
        }
        const speed = 2 + 6 * (1 - i / (g.nx - 1)) + Math.sin((i + j + k) / 3);
        const w = windUV(speed, dir + 10 * Math.sin(j / 4));
        uk.push(Math.round(w.u / WIND_UNIT_MS));
        vk.push(Math.round(w.v / WIND_UNIT_MS));
      }
    u.push(uk);
    v.push(vk);
  }
  return {
    model: "meteoswiss-icon-ch",
    fetchedAt: new Date(start).toISOString(),
    runAt: new Date(start - 3 * 3_600_000).toISOString(),
    times,
    grid: { ...g },
    u,
    v,
    attribution: WIND_ATTRIBUTION,
  };
}

/**
 * Synthetic precipitation field matching makeWindFieldFixture (same hour, grid and run): a band of
 * rain that moves from the south-west (Léman) to the north-east over the hours, heaviest in its
 * middle (every class from light to very heavy appears, already in the first hour), dry
 * elsewhere. `times` are the ENDS of the hourly sums, starting at the end of the UTC hour
 * containing `nowMs`, and like the live API there is no sum for the wind fixture's last hour. The
 * same far south-east corner as the wind fixture is null ("draw nothing there").
 */
export function makePrecipFieldFixture(nowMs: number = Date.now()): PrecipField {
  const start = Math.floor(nowMs / 3_600_000) * 3_600_000;
  const g = WIND_GRID;
  const times: string[] = [];
  const p: (number | null)[][] = [];
  for (let k = 0; k < WIND_HOURS - 1; k++) {
    times.push(new Date(start + (k + 1) * 3_600_000).toISOString());
    // band centre moves along the SW→NE diagonal (over Léman/Neuchâtel at first); distance in grid
    // cells from it
    const c = 14 + (k * (g.nx + g.ny - 20)) / (WIND_HOURS - 2);
    const pk: (number | null)[] = [];
    for (let j = 0; j < g.ny; j++)
      for (let i = 0; i < g.nx; i++) {
        if (i >= g.nx - 3 && j <= 1) {
          pk.push(null);
          continue;
        }
        const d = Math.abs(i + j - c);
        pk.push(precipUnits(d > 6 ? 0 : 14 * (1 - d / 6) ** 2));
      }
    p.push(pk);
  }
  return {
    model: "meteoswiss-icon-ch",
    fetchedAt: new Date(start).toISOString(),
    runAt: new Date(start - 3 * 3_600_000).toISOString(),
    times,
    grid: { ...g },
    p,
    attribution: PRECIP_ATTRIBUTION,
  };
}
